import type { PartyPlan } from "./types/parties-api";
import type { BlockedJoinEventType } from "./blocked-join-events";
import {
  type PartyPassCheckoutSource,
  type PartyPassUpsellReason,
  partyPassUpsellReasonFromCheckoutSource,
  partyPassUpsellSurfaceFromCheckoutSource,
} from "./party-pass-checkout";
import type {
  JevDecisionLayerCta,
  JevDecisionPage,
  JevDecisionSurfaceKind,
  JevPassIntensity,
  JevPassReason,
  JevPreShowFocus,
} from "./jev-decision-layer";

export const CHAT_MESSAGE_SENT_EVENT = "chat_message_sent";
export const CHAT_OPENED_EVENT = "chat_opened";
export const LIVE_REACTION_SENT_EVENT = "live_reaction_sent";
export const PARTY_PASS_UPSELL_VIEWED_EVENT = "party_pass_upsell_viewed";
export const JOIN_BLOCKED_EVENT = "join_blocked";
export const PARTY_RECAP_PAGE_VIEWED_EVENT = "party_recap_page_viewed";
export const PARTY_RECAP_CTA_CLICKED_EVENT = "party_recap_cta_clicked";
export const LEADERBOARD_VIEWED_EVENT = "leaderboard_viewed";
export const DECISION_LAYER_VIEWED_EVENT = "decision_layer_viewed";
export const DECISION_LAYER_CLICKED_EVENT = "decision_layer_clicked";

export const DECISION_LAYER_CLICK_CONTROLS = [
  "banner",
  "pass_unlock",
  "primary",
  "featured_chat",
] as const;

export type DecisionLayerClickControl =
  (typeof DECISION_LAYER_CLICK_CONTROLS)[number];

export type DecisionLayerBrain =
  | "growth"
  | "pass"
  | "show_night"
  | "pre_show"
  | "home_chat";

export const CHAT_OPENED_ENTRIES = [
  "tab",
  "deeplink",
  "nudge",
  "celebration",
  "show_night",
  "preview",
  "decision_layer",
] as const;
export type ChatOpenedEntry = (typeof CHAT_OPENED_ENTRIES)[number];

export type PartyAnalyticsContextInput = {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
};

export type PartyAnalyticsContext = {
  party_id: string;
  award_show_id?: string;
  plan?: PartyPlan;
  participant_count?: number;
};

function isPresentString(value: string | null | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

export function buildPartyAnalyticsContext(
  input: PartyAnalyticsContextInput,
): PartyAnalyticsContext {
  return {
    party_id: input.partyId,
    ...(isPresentString(input.awardShowId)
      ? { award_show_id: input.awardShowId.trim() }
      : {}),
    ...(input.plan === "free" || input.plan === "party_pass"
      ? { plan: input.plan }
      : {}),
    ...(isFiniteNumber(input.participantCount)
      ? { participant_count: input.participantCount }
      : {}),
  };
}

export function parseChatOpenedEntry(value: unknown): ChatOpenedEntry {
  if (
    typeof value === "string" &&
    (CHAT_OPENED_ENTRIES as readonly string[]).includes(value)
  ) {
    return value as ChatOpenedEntry;
  }
  return "deeplink";
}

export function partyChatHref(
  partyId: string,
  entry: ChatOpenedEntry = "deeplink",
): string {
  const path = `/parties/${partyId}/chat`;
  if (entry === "deeplink") {
    return path;
  }
  return `${path}?entry=${entry}`;
}

export function buildChatMessageSentEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
  messageLength: number;
  isHost: boolean;
}): PartyAnalyticsContext & {
  message_length: number;
  is_host: boolean;
} {
  return {
    ...buildPartyAnalyticsContext(input),
    message_length: input.messageLength,
    is_host: input.isHost,
  };
}

export function buildChatOpenedEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
  entry: ChatOpenedEntry;
}): PartyAnalyticsContext & { entry: ChatOpenedEntry } {
  return {
    ...buildPartyAnalyticsContext(input),
    entry: input.entry,
  };
}

export function buildLiveReactionSentEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
  reactionType: string;
  categoryId?: string | null;
}): PartyAnalyticsContext & {
  reaction_type: string;
  category_id?: string;
} {
  return {
    ...buildPartyAnalyticsContext(input),
    reaction_type: input.reactionType,
    ...(isPresentString(input.categoryId)
      ? { category_id: input.categoryId.trim() }
      : {}),
  };
}

export function buildPartyPassUpsellViewedEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
  reason: PartyPassUpsellReason;
  surface: string;
}): PartyAnalyticsContext & {
  reason: PartyPassUpsellReason;
  surface: string;
} {
  return {
    ...buildPartyAnalyticsContext(input),
    reason: input.reason,
    surface: input.surface,
  };
}

export function buildPartyPassCheckoutStartedEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
  source: PartyPassCheckoutSource;
  amountCents: number | null;
}): PartyAnalyticsContext & {
  source: PartyPassCheckoutSource;
  surface: string;
  reason: PartyPassUpsellReason;
  amount_cents: number | null;
} {
  return {
    ...buildPartyAnalyticsContext(input),
    source: input.source,
    surface: partyPassUpsellSurfaceFromCheckoutSource(input.source),
    reason: partyPassUpsellReasonFromCheckoutSource(input.source),
    amount_cents: input.amountCents,
  };
}

function toTimestamp(value: Date | string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
}

export function hoursToPaid(
  createdAt: Date | string | null | undefined,
  paidAt: Date | string | null | undefined,
): number | null {
  const createdMs = toTimestamp(createdAt);
  const paidMs = toTimestamp(paidAt);
  if (createdMs == null || paidMs == null) {
    return null;
  }
  return Math.round(((paidMs - createdMs) / 3_600_000) * 100) / 100;
}

export function buildJoinBlockedEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
  blockType: BlockedJoinEventType;
}): PartyAnalyticsContext & { block_type: BlockedJoinEventType } {
  return {
    ...buildPartyAnalyticsContext(input),
    block_type: input.blockType,
  };
}

export function buildPartyRecapEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
  source?: string | null;
  pageType?: string | null;
}): PartyAnalyticsContext & {
  source?: string;
  page_type?: string;
} {
  return {
    ...buildPartyAnalyticsContext(input),
    ...(isPresentString(input.source) ? { source: input.source } : {}),
    ...(isPresentString(input.pageType) ? { page_type: input.pageType } : {}),
  };
}

export function buildLeaderboardViewedEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
}): PartyAnalyticsContext {
  return buildPartyAnalyticsContext(input);
}

export function buildDecisionLayerViewedEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
  page: JevDecisionPage;
  brain: "growth" | "pass" | "show_night";
  surfaceKind: JevDecisionSurfaceKind;
  cta: JevDecisionLayerCta | null;
  passReason: Exclude<JevPassReason, "none"> | null;
  passIntensity: Exclude<JevPassIntensity, "stay_quiet"> | null;
  tone: "gentle" | "prominent" | null;
}): PartyAnalyticsContext & {
  page: JevDecisionPage;
  brain: "growth" | "pass" | "show_night";
  surface: JevDecisionSurfaceKind;
  cta: JevDecisionLayerCta | null;
  pass_reason: Exclude<JevPassReason, "none"> | null;
  pass_intensity: Exclude<JevPassIntensity, "stay_quiet"> | null;
  tone: "gentle" | "prominent" | null;
} {
  return {
    ...buildPartyAnalyticsContext(input),
    page: input.page,
    brain: input.brain,
    surface: input.surfaceKind,
    cta: input.cta,
    pass_reason: input.passReason,
    pass_intensity: input.passIntensity,
    tone: input.tone,
  };
}

export function buildDecisionLayerClickedEventProperties(input: {
  partyId: string;
  awardShowId?: string | null;
  plan?: PartyPlan | null;
  participantCount?: number | null;
  page: JevDecisionPage;
  brain: DecisionLayerBrain;
  surfaceKind: JevDecisionSurfaceKind;
  cta: JevDecisionLayerCta | null;
  passReason: Exclude<JevPassReason, "none"> | null;
  passIntensity: Exclude<JevPassIntensity, "stay_quiet"> | null;
  tone: "gentle" | "prominent" | null;
  control: DecisionLayerClickControl;
  preShowFocus?: JevPreShowFocus | null;
}): PartyAnalyticsContext & {
  page: JevDecisionPage;
  brain: DecisionLayerBrain;
  surface: JevDecisionSurfaceKind;
  cta: JevDecisionLayerCta | null;
  pass_reason: Exclude<JevPassReason, "none"> | null;
  pass_intensity: Exclude<JevPassIntensity, "stay_quiet"> | null;
  tone: "gentle" | "prominent" | null;
  control: DecisionLayerClickControl;
  pre_show_focus: JevPreShowFocus | null;
} {
  return {
    ...buildPartyAnalyticsContext(input),
    page: input.page,
    brain: input.brain,
    surface: input.surfaceKind,
    cta: input.cta,
    pass_reason: input.passReason,
    pass_intensity: input.passIntensity,
    tone: input.tone,
    control: input.control,
    pre_show_focus: input.preShowFocus ?? null,
  };
}

export type DecisionLayerClickedEventProperties = ReturnType<
  typeof buildDecisionLayerClickedEventProperties
>;
